import * as React from 'react'

import { Box, Button, Divider, NumberBadge, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import EmptyState from 'modules/core/components/MultiFilters/EmptyState'
import EmptyRow from 'modules/core/components/MultiFilters/EmptyState/EmptyRow'
import FilterBox from 'modules/core/components/MultiFilters/FilterItem/FilterBox'
import FilterRow from 'modules/core/components/MultiFilters/FilterItem/FilterRow'
import type LogicOperatorForm from 'modules/core/components/MultiFilters/Forms/LogicOperatorForm'
import type MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import type { FilterOption } from 'modules/core/components/MultiFilters/types'
import i18n from 'modules/core/lib/i18n'

interface Props {
  fields: readonly FilterOption[]
  filters: MultiFilterForm[]
  logicOperator: LogicOperatorForm
  onAddFilter: () => void
  onRemoveFilter: (index: number) => void
  onApplyFilters: () => void
  onCancel: () => void
  emptyStateLabel?: string
  addFilterLabel?: string
  submitLabel?: string
  cancelLabel?: string
  topButtons?: boolean
  rowItems?: boolean
}

const FilterPanel = ({
  fields,
  filters,
  logicOperator,
  onAddFilter,
  onRemoveFilter,
  onApplyFilters,
  onCancel,
  emptyStateLabel = '',
  addFilterLabel = i18n.t('filters.multi_filters.cta.add'),
  submitLabel = i18n.t('filters.multi_filters.cta.submit'),
  cancelLabel = i18n.t('filters.multi_filters.cta.cancel'),
  topButtons = false,
  rowItems = true,
}: Props) => {
  const padding = rowItems ? 's24' : undefined

  const showTopButtons = () => {
    return (
      <Box
        border={{ bottom: { width: 's1', style: 'solid', color: 'darker160' } }}
        borderRadius={{ topLeft: 'abs008', topRight: 'abs008' }}
        background="grey200"
        paddingY="s12"
        paddingX="s16"
        justifyContent="spaceBetween"
        alignItems="center"
        flexDirection="row"
        flexWrap="wrap"
      >
        <Box flexDirection="row" gap="s8">
          <NumberBadge label={filters.length} />
          <Text.Normal>{i18n.t('filters.multi_filters.title')}</Text.Normal>
        </Box>
        <Box flexDirection="row" gap="s8">
          <Button onClick={onCancel} type="secondary">
            {cancelLabel}
          </Button>
          <Button onClick={onApplyFilters}>{submitLabel}</Button>
        </Box>
      </Box>
    )
  }

  const showBottomButtons = () => {
    return (
      <Box
        border={{ top: { width: 's1', style: 'solid', color: 'darker160' } }}
        borderRadius={{ bottomLeft: 'abs008', bottomRight: 'abs008' }}
        background="grey200"
        paddingY="s12"
        paddingX="s32"
        justifyContent="flexEnd"
        alignItems="center"
        flexDirection="row"
        flexWrap="wrap"
        gap="s8"
      >
        <Button onClick={onCancel} type="secondary">
          {cancelLabel}
        </Button>
        <Button onClick={onApplyFilters} type="primary">
          {submitLabel}
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      {topButtons && showTopButtons()}
      {filters.length > 0 ? (
        <Box padding={padding} gap="s16">
          {filters.map((filter, index) => {
            if (rowItems) {
              return (
                <FilterRow
                  key={index}
                  index={index}
                  fields={fields}
                  filter={filter}
                  logicOperator={logicOperator}
                  onRemoveFilter={onRemoveFilter}
                />
              )
            } else {
              return (
                <div key={index}>
                  <FilterBox
                    key={index}
                    index={index}
                    fields={fields}
                    filter={filter}
                    logicOperator={logicOperator}
                    onRemoveFilter={onRemoveFilter}
                  />
                  <Divider />
                </div>
              )
            }
          })}
          <EmptyRow />
        </Box>
      ) : (
        <EmptyState label={emptyStateLabel} />
      )}
      <Box
        paddingX="s24"
        paddingBottom="s24"
        justifyContent="flexStart"
        alignItems="center"
        flexDirection="row"
        flexWrap="wrap"
      >
        <Button onClick={onAddFilter} type="secondary" iconName="add">
          {addFilterLabel}
        </Button>
      </Box>
      {!topButtons && showBottomButtons()}
    </Box>
  )
}

export default observer(FilterPanel)
